<template>
  <v-container>
    <v-row>
      <v-col>
        <v-text-field outlined dense readonly :value="geotagName" label="Geotag"/>
      </v-col>

      <v-col>
        <v-autocomplete
          v-model="select.poiName"
          :items="selectItems.poi"
          :search-input.sync="search.poiName"
          outlined
          dense
          chips
          small-chips
          label="POI"
          clearable
          hide-selected
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar v-if="item"
                                color="indigo"
                                class="headline font-weight-light white--text"
            >
              {{ item.charAt(0) }}
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-text="item"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col>
        <v-autocomplete
          v-model="select.priority"
          :items="[1,2,3,4,'empty']"
          label="POI priority"
          outlined
          clearable
          multiple
          dense
          chips
          small-chips
          deletable-chips
          hide-selected
          :menu-props="{
            closeOnContentClick: true,
          }"
        />
      </v-col>

      <v-col>
        <v-autocomplete
          v-model="select.poiTag"
          :items="selectItems.poiTag"
          :search-input.sync="search.poiTag"
          outlined
          dense
          chips
          small-chips
          label="POI Tag"
          return-object
          clearable
          hide-selected
          item-text="name"
        >
          <template v-slot:item="{ item }">
            <v-list-item-avatar v-if="item.name"
                                color="indigo"
                                class="headline font-weight-light white--text"
            >
              {{ item.name.charAt(0) }}
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title v-text="item.name"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col>
        <v-btn
          class="mx-2"
          fab
          small
          color="primary"
          @click="getPoiImages"
        >
          <v-icon dark>
            mdi-magnify
          </v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-sheet
      class="mx-auto"
      elevation="8"
    >
      <v-progress-linear
        indeterminate
        color="cyan"
        :active="searchloading"
      />

      <v-slide-group
        class="pa-4"
        show-arrows
      >
        <v-slide-item
          v-for="image in searchResults"
          :key="image.id"
          class="ma-2"
          v-slot="{ active, toggle }"
        >
          <draggable
            :group="{ name: 'imagecards', put: false }"
            :list="[image]"
          >
            <v-card
              max-height="150"
              max-width="100"
              @click="toggle"
            >
              <v-img
                class="white--text align-end"
                :src="`${image.public_link}?thumb=1`"
                height="150"
                contain
              />
              <v-card-actions v-if="active" class="card-actions">
                <v-btn
                  color="orange"
                  text
                  small
                  @click="addImage(image)"
                >
                  Add
                </v-btn>
              </v-card-actions>
            </v-card>
          </draggable>
        </v-slide-item>
      </v-slide-group>
    </v-sheet>

    <image-with-border-dialog ref="imageWithBorderDialog"/>

    <v-row>
      <v-col>
        <draggable
          v-model="geotagImages"
          class="playlist-content"
          :group="{ name: 'imagecards' }"
          justify="start"
          @change="addDraggedImage"
        >
          <v-card
            v-for="image in geotagImages" :key="image.id"
            height="150"
            width="100"
            class="ma-5"
          >
            <v-img
              class="white--text align-end"
              :src="`${image.public_link}?thumb=1`"
              height="150"
              contain
              @click="$refs.imageWithBorderDialog.openDialog('image', {image: image})"
            >
              <template v-slot:placeholder>
                <v-row
                  class="fill-height ma-0"
                  align="center"
                  justify="center"
                >
                  <v-progress-circular
                    indeterminate
                    color="black"
                  ></v-progress-circular>
                </v-row>
              </template>
            </v-img>
            <v-card-actions class="card-actions">
              <v-btn
                color="orange"
                icon
                @click="deleteImage(image.id)"
              >
                <v-icon
                  color="red"
                >
                  mdi-delete
                </v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </draggable>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>

import { mapActions } from 'vuex';
import draggable from 'vuedraggable';
import ImageWithBorderDialog from '@/components/dialogs/ImageWithBorderDialog.vue';
import debounce from 'lodash.debounce';

export default {
  name: 'MediaGeotagPoiImages',
  components: {
    ImageWithBorderDialog, draggable,
  },
  props: {
    geotagId: {
      type: Number,
    },
    geotagName: {
      type: String,
    },
    sequencedGeotags: {
      type: Array,
    },
  },
  data() {
    return {
      select: {
        poiName: null,
        priority: [],
        poiTag: null,
        geotagId: null,
        withNullGeotag: true,
      },
      search: {
        poiName: null,
        poiTag: null,
        geotag: null,
      },
      searchloading: false,
      selectItems: {
        poi: [],
        poiTag: [],
        geotag: [],
      },
      searchResults: [],
      geotagImages: [],
      itemClone: {},
    };
  },
  created() {
    this.getGeotagImages(this.geotagId);
    this.debouncedSearchPoi = debounce(this.searchPoi, 500);
    this.debouncedSearchPoiTag = debounce(this.searchPoiTag, 500);
    this.select.geotagId = this.geotagId;
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    getGeotagImages(geotagId) {
      this.$requestHelpers
        .get(`api/admin/geotag/poiImage/${geotagId}`)
        .then((response) => {
          this.geotagImages = response.data.result;
        });
    },

    searchPoi(val) {
      this.$requestHelpers.get('api/admin/pois/searchByTermInSequence',
        {
          params: {
            term: val,
            geotag_id: this.geotagId,
          },
        })
        .then((response) => {
          this.selectItems.poi = response.data;
        });
    },

    searchPoiTag(val) {
      this.$requestHelpers.get('api/admin/poitag/searchByTerm',
        { params: { term: val } })
        .then((response) => {
          this.selectItems.poiTag = response.data;
        });
    },

    getPoiImages() {
      this.searchloading = true;

      this.$requestHelpers.get('api/admin/poi/getImages',
        { params: this.select })
        .then((response) => {
          this.searchResults = response.data.filter(
            (searchRes) => this
              .geotagImages.find((image) => image.id === searchRes.id) === undefined,
          );
          this.searchloading = false;
        });
    },

    addImage(image) {
      this.$requestHelpers.post(`api/admin/geotag/poiImage/${this.geotagId}`, {
        add: image.id,
      }).then(() => {
        this.geotagImages.push(image);
        this.searchResults = this.searchResults.filter(
          (searchImage) => searchImage.id !== image.id,
        );
      });
    },

    addDraggedImage(evt) {
      if (!evt.added) {
        return;
      }
      const addedImage = evt.added.element;
      const rollback = () => {
        this.geotagImages = this.geotagImages.filter(
          (image) => image.id !== addedImage.id,
        );
        if (!this.searchResults.find((image) => image.id === addedImage.id)) {
          this.searchResults.push(addedImage);
        }
      };

      this.$requestHelpers.post(`api/admin/geotag/poiImage/${this.geotagId}`, {
        add: evt.added.element.id,
      }).then(() => {
        this.searchResults = this.searchResults.filter(
          (searchImage) => searchImage.id !== addedImage.id,
        );
      }).catch(() => {
        rollback();
      });
    },

    deleteImage(id) {
      this.$confirm('Are you sure you want to delete this image from geotag?').then((res) => {
        if (!res) return;
        this.$requestHelpers.post(`api/admin/geotag/poiImage/${this.geotagId}`, {
          delete: id,
        }).then(() => {
          this.geotagImages = this.geotagImages.filter((image) => image.id !== id);
        });
      });
    },
  },

  watch: {
    geotagId(newValue) {
      this.getGeotagImages(newValue);
    },
    // eslint-disable-next-line func-names
    'search.poiTag': function (val) {
      if (!val || val.length < 2) return;
      this.debouncedSearchPoiTag(val);
    },
    // eslint-disable-next-line func-names
    'search.poiName': function (val) {
      if (!val || val.length < 2) return;
      this.debouncedSearchPoi(val);
    },
  },
};

</script>

<style scoped>
.playlist-content {
  width: 100%;
  display: -ms-flexbox;
  display: -webkit-flex;
  display: flex;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -webkit-justify-content: flex-start;
  -ms-flex-pack: start;
  justify-content: flex-start;
  -webkit-align-content: flex-start;
  -ms-flex-line-pack: start;
  align-content: flex-start;
  -webkit-align-items: flex-start;
  -ms-flex-align: start;
  align-items: flex-start;
}
.card-actions {
  position: absolute;
  top: 0;
  right: 0;
  padding: 0;
}
</style>
